import React, { useCallback, useState, useEffect } from "react";
import { connect, useSelector } from "react-redux";
import { Menu, Icon, Layout, Badge } from "antd";
import { Link } from "react-router-dom";
import { useIntl } from "react-intl";

import {
  isLoginUser,
  isDocumentUser,
  notDocumentUser,
  isDocsAdminUser,
} from "../../../modules/session";
import { ReactComponent as User } from "../../icons/user.svg";
import { ReactComponent as List } from "../../icons/list.svg";
import { ReactComponent as Parcel } from "../../icons/parcel.svg";
import { ReactComponent as Invoice } from "../../icons/invoice.svg";
import { ReactComponent as Finance } from "../../icons/finance.svg";
import { ReactComponent as Document } from "../../icons/document.svg";
import { ReactComponent as Settings } from "../../icons/settings.svg";
import { ReactComponent as Tracking } from "../../icons/tracking.svg";
import { ReactComponent as Management } from "../../icons/management.svg";
import { ReactComponent as Notification } from "../../icons/notification.svg";

import "./index.css";

const { SubMenu } = Menu;

const rootSubMenuKeys = ["sub1", "sub2", "sub3"];

const mapStateToProps = ({ session, router }) => ({
  authenticated: session.authenticated,
  pathname: router.location.pathname,
});

const SideBar = ({ authenticated, pathname, countOfDocs }) => {
  const intl = useIntl();
  const { data } = useSelector((state) => state.client);
  const { user } = useSelector((state) => state.session);

  const docsUser = isDocumentUser(user?.role);
  const docsAdmin = isDocsAdminUser(user?.role);
  const notDocsUser = notDocumentUser(user?.role);
  const notificationUser = user && user.notifications;

  const [openKeys, setOpenKeys] = useState([]);
  const onOpenChange = useCallback(
    (newOpenKeys) => {
      const latestOpenKey = newOpenKeys.find(
        (key) => openKeys.indexOf(key) === -1,
      );
      if (rootSubMenuKeys.indexOf(latestOpenKey) === -1) {
        setOpenKeys(newOpenKeys);
      } else {
        setOpenKeys(latestOpenKey ? [latestOpenKey] : []);
      }
    },
    [openKeys, setOpenKeys],
  );

  useEffect(() => {
    pathname === "/" && setOpenKeys([]);
  }, [pathname]);

  return authenticated ? (
    <Layout.Sider theme="light" breakpoint="xxl">
      <Menu
        mode="inline"
        openKeys={openKeys}
        className="sidebar-menu"
        selectedKeys={[pathname]}
        onOpenChange={onOpenChange}
      >
        {notDocsUser && (
          <SubMenu
            key="sub1"
            title={
              <span>
                <Icon component={Parcel} className="sidebar-icon" />
                <span>
                  {intl.formatMessage({
                    id: "menu.newConsignment",
                    defaultMessage: "Nová zásilka",
                  })}
                </span>
              </span>
            }
          >
            <Menu.Item key="/new-consignment">
              <Link to="/new-consignment">
                {intl.formatMessage({
                  id: "menu.newConsignment.new",
                  defaultMessage: "Vytvořit",
                })}
              </Link>
            </Menu.Item>
            <Menu.Item key="/consignment-import">
              <Link to="/consignment-import">
                {intl.formatMessage({
                  id: "menu.newConsignment.import",
                  defaultMessage: "Import dat",
                })}
              </Link>
            </Menu.Item>
            <Menu.Item key="/consignment-templates">
              <Link to="/consignment-templates">
                {intl.formatMessage({
                  id: "menu.newConsignment.template",
                  defaultMessage: "Ze šablony",
                })}
              </Link>
            </Menu.Item>
          </SubMenu>
        )}
        {notDocsUser && (
          <SubMenu
            key="sub2"
            title={
              <span>
                <Icon component={List} className="sidebar-icon" />
                <span>
                  {intl.formatMessage({
                    id: "menu.consignmentList",
                    defaultMessage: "Přehled zásilek",
                  })}
                </span>
              </span>
            }
          >
            <Menu.Item key="/work-consignments">
              <Link to="/work-consignments">
                {intl.formatMessage({
                  id: "menu.consignmentList.notSent",
                  defaultMessage: "Neodeslané do DHL",
                })}
              </Link>
            </Menu.Item>
            <Menu.Item key="/consignments">
              <Link to="/consignments">
                {intl.formatMessage({
                  id: "menu.consignmentList.sent",
                  defaultMessage: "Odeslané do DHL",
                })}
              </Link>
            </Menu.Item>
          </SubMenu>
        )}
        {notDocsUser && (
          <Menu.Item key="/track-consignments">
            <Link to="/track-consignments">
              <Icon component={Tracking} className="sidebar-icon" />
              <span>
                {intl.formatMessage({
                  id: "menu.tracking",
                  defaultMessage: "Sledování zásilek",
                })}
              </span>
            </Link>
          </Menu.Item>
        )}
        {notDocsUser && (
          <Menu.Item key="/customers">
            <Link to="/customers">
              <Icon component={User} className="sidebar-icon" />
              <span>
                {intl.formatMessage({
                  id: "menu.customers",
                  defaultMessage: "Seznam zákazníků",
                })}
              </span>
            </Link>
          </Menu.Item>
        )}
        {notDocsUser && (
          <Menu.Item key="/calculator">
            <Link to="/calculator">
              <Icon component={Finance} className="sidebar-icon" />
              <span>
                {intl.formatMessage({
                  id: "menu.calculator",
                  defaultMessage: "Kalkulátor ceny",
                })}
              </span>
            </Link>
          </Menu.Item>
        )}
        {data && data.contractSetting && (
          <Menu.Item key="/contracts" className="menu-two-lines">
            <Link to="/contracts">
              <Icon component={Document} className="sidebar-icon" />
              <span>
                {intl.formatMessage({
                  id: "menu.contracts",
                  defaultMessage: "Obchodní podmínky a smlouvy",
                })}
              </span>
            </Link>
          </Menu.Item>
        )}
        {/* <Menu.Item key="/invoices">
            <Link to="/invoices">
              <Icon component={Invoice} className="sidebar-icon" />
              <span>
                {intl.formatMessage({
                  id: "menu.invoices",
                  defaultMessage: "Přehled faktur",
                })}
              </span>
            </Link>
          </Menu.Item> */}
        {notDocsUser && (
          <SubMenu
            key="sub3"
            title={
              <span>
                <Icon component={Settings} className="sidebar-icon" />
                <span>
                  {intl.formatMessage({
                    id: "menu.setting",
                    defaultMessage: "Nastavení",
                  })}
                </span>
              </span>
            }
          >
            <Menu.Item key="/users">
              <Link to="/users">
                {intl.formatMessage({
                  id: "menu.setting.users",
                  defaultMessage: "Správa uživatelů",
                })}
              </Link>
            </Menu.Item>
            {isLoginUser(user?.role) && (
              <Menu.Item key="/one-time-login">
                <Link to="/one-time-login">
                  {intl.formatMessage({
                    id: "menu.setting.oneTimeLogin",
                    defaultMessage: "Jednorázové přihlášení",
                  })}
                </Link>
              </Menu.Item>
            )}
            <Menu.Item key="/change-password">
              <Link to="/change-password">
                {intl.formatMessage({
                  id: "menu.setting.changePassword",
                  defaultMessage: "Změna hesla",
                })}
              </Link>
            </Menu.Item>
          </SubMenu>
        )}
        {docsUser && (
          <Menu.Item key="/documents">
            <Link to="/documents">
              <Icon component={Invoice} className="sidebar-icon" />
              <span>
                {intl.formatMessage({
                  id: "menu.documents",
                  defaultMessage: "Dokumenty",
                })}
                <Badge count={countOfDocs} className="document-badge" />
              </span>
            </Link>
          </Menu.Item>
        )}
        {notificationUser && (
          <Menu.Item key="/notifications">
            <Link to="/notifications">
              <Icon component={Notification} className="sidebar-icon" />
              <span>
                {intl.formatMessage({
                  id: "menu.notifications",
                  defaultMessage: "Notifikace",
                })}
              </span>
            </Link>
          </Menu.Item>
        )}
        {docsAdmin && (
          <Menu.Item key="/new-document">
            <Link to="/new-document">
              <Icon component={Management} className="sidebar-icon" />
              <span>
                {intl.formatMessage({
                  id: "menu.documents.new",
                  defaultMessage: "Nový dokument",
                })}
              </span>
            </Link>
          </Menu.Item>
        )}
      </Menu>
    </Layout.Sider>
  ) : (
    <></>
  );
};

export default connect(mapStateToProps, {})(SideBar);
